import {
  Card,
  Text,
  Box,
  Select,
  InlineStack,
  Button,
  SkeletonBodyText,
  Loading,
} from "@medentic/polaris-react";
import { XIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useParams } from "react-router-dom";

interface AssignDoctorProps {
  doctorId: string;
  doctorOptions: Array<{
    label: string;
    value: string;
  }>;
  loading?: boolean;
}

function AssignDoctor({ doctorId, doctorOptions, loading }: AssignDoctorProps) {
  const { t } = useTranslation();
  const [selectedDoctorId, setSelectedDoctorId] = useState(doctorId);

  // Sync local state with prop when it changes
  useEffect(() => {
    setSelectedDoctorId(doctorId);
  }, [doctorId]);

  const { clinicId, patientId } = useParams<{
    patientId: string;
    clinicId: string;
  }>();
  const { PATCH } = useApiRequest();
  const queryClient = useQueryClient();

  const { mutate: updateDoctorMutation, isPending: isSaving } = useMutation({
    mutationFn: (data: { core_patient_owner: string }) =>
      PATCH(
        `/api/healthcare-companies/${clinicId}/patients/${patientId}`,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patientDetails", patientId],
      });
      queryClient.invalidateQueries({ queryKey: ["patients"] });
    },
    onError: (error) => {
      console.error("Error updating assigned doctor:", error);
    },
  });

  const handleChange = (value: string) => {
    setSelectedDoctorId(value);
    updateDoctorMutation({
      core_patient_owner: value,
    });
  };

  const handleRemoveDoctor = () => {
    setSelectedDoctorId("");
    updateDoctorMutation({
      core_patient_owner: "",
    });
  };

  if (loading) {
    return (
      <Card>
        <Box paddingBlockEnd={"200"}>
          <Text as="h1" variant="headingMd">
            {t("patientDetails.assignedDoctor")}
          </Text>
        </Box>
        <SkeletonBodyText lines={2} />
      </Card>
    );
  }

  return (
    <Card>
      <Box paddingBlockEnd={"200"}>
        <Text as="h1" variant="headingMd">
          {t("patientDetails.assignedDoctor")}
        </Text>
      </Box>
      <InlineStack align="space-between" blockAlign="center" gap="100">
        <Box width={selectedDoctorId ? "85%" : "100%"}>
          <Select
            label=""
            labelHidden
            options={[
              { label: t("patientDetails.noAssignedDoctor"), value: "" },
              ...doctorOptions,
            ]}
            value={selectedDoctorId}
            onChange={handleChange}
            disabled={isSaving}
          />
        </Box>
        {selectedDoctorId && (
          <Box width="10%">
            <Button
              icon={XIcon}
              onClick={handleRemoveDoctor}
              variant="plain"
              disabled={isSaving}
              accessibilityLabel={t("patientDetails.actions.removeDoctor")}
            />
          </Box>
        )}
      </InlineStack>
      {isSaving && <Loading />}
    </Card>
  );
}

export default AssignDoctor;
